import SharedDialog from '../../../shared/shared-dialog';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import { useState } from 'react';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { isAbleToSeeUIElement } from '../../../utils/roles/client-roles';
import { BillingRoles } from '../../../utils/roles/client-roles';
import {
  attachAchToCustomerInStripe,
  BillingKeys,
} from '../../../api/billing-api';
import { useMutation } from '@tanstack/react-query';
import { useAnalytics } from '../../../shared/analytics/create-events';
import { useStore } from '../../state/zustand-store';
import { useQueryClient } from '@tanstack/react-query';
import { handleErrors } from '../../../utils/errors';
import { ModuleList } from '../../../shared/analytics/module-list';
import { attachCardToCustomerInStripe } from '../../../api/billing-api';
import {
  BillingInformationModel,
  StripeAchPaymentSourceModel,
} from '../../../api/billing-types';
import * as analytics from '../../../shared/analytics/analytics.json';
import { BankAccountPaymentMethodForm } from './bank-account-payment-method-form';
import { SubmitHandler, useForm } from 'react-hook-form';
import AddCreditCardForm from './add-credit-card-form';
import PoweredByStripe from '../../../assets/powered-by-stripe.svg';
import PoweredByStripeWhite from '../../../assets/powered-by-stripe-white.svg';
import { HubSpotCompany } from '../../../api/billing-types';

import Box from '@mui/material/Box';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import { useTheme } from '@mui/material/styles';

interface AddPaymentMethodDialogProps {
  open: boolean;
  handleClose: () => void;
  changeLogRoles: string[];
  billingInfoModel: BillingInformationModel;
  hubSpotCompany: HubSpotCompany;
}

export default function AddPaymentMethodDialog({
  open,
  handleClose,
  changeLogRoles,
  billingInfoModel,
  hubSpotCompany,
}: AddPaymentMethodDialogProps) {
  const { createEvent } = useAnalytics();
  const { setSnackbar } = useStore();
  const queryClient = useQueryClient();
  const theme = useTheme();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const [sourceToken, setSourceToken] = useState('');

  const creditCardPaymentsEnabled =
    hubSpotCompany.properties.credit_card_payments_enabled;

  const [paymentMethod, setPaymentMethod] = useState<'card' | 'ach'>(
    !creditCardPaymentsEnabled ? 'ach' : 'card'
  );

  const stripe = useStripe();
  const elements = useElements();

  const {
    control,
    getValues,
    setValue,
    watch,
    handleSubmit,
    formState: { errors },
  } = useForm<Partial<StripeAchPaymentSourceModel>>({
    defaultValues: {
      bank_name: '',
      routing_number: '',
      account_number: '',
      account_holder_name: '',
      account_holder_type: '',

      // StripePaymentSourceModel
      address_city: '',
      address_country: 'United States',
      address_line1: '',
      address_state: '',
      address_zip: '',
      country: '',

      metadata: {
        nickName: '',
      },
    },
  });

  const selectedCountry = watch('address_country');

  const { mutate, isPending } = useMutation({
    mutationFn: (sourceToken: string) =>
      attachCardToCustomerInStripe(
        billingInfoModel.customerInfoModel?.stripeCustomerId || '',
        sourceToken,
        getValues().metadata?.nickName || '',
        billingInfoModel,
        changeLogMessage
      ),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'Payment method added successfully!',
      });

      setChangeLogMessage('');
      handleClose();

      queryClient.invalidateQueries({
        queryKey: [BillingKeys.CustomerBillingInformation],
      });
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue adding the payment method. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(analytics.billing.payments.add_payment_method, {
        module: ModuleList.modules.billing.name,
        section: ModuleList.modules.billing.sections.payments,
        paymentMethod: 'card',
      });
    },
  });

  const { mutate: mutateAch, isPending: isPendingAch } = useMutation({
    mutationFn: (data: Partial<StripeAchPaymentSourceModel>) =>
      attachAchToCustomerInStripe(
        {
          ...data,
          stripePaymentSourceType: 'Ach',
          customer: {
            id: billingInfoModel.customerInfoModel?.stripeCustomerId || '',
          },
        },
        billingInfoModel,
        changeLogMessage
      ),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'Payment method added successfully!',
      });

      queryClient.invalidateQueries({
        queryKey: [BillingKeys.CustomerBillingInformation],
      });

      setChangeLogMessage('');
      setOpenChangeLogDialog(false);
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue adding the payment method. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(analytics.billing.payments.add_payment_method, {
        module: ModuleList.modules.billing.name,
        section: ModuleList.modules.billing.sections.payments,
        paymentMethod: 'ach',
      });
    },
  });

  async function getStripeTokenForCC(
    data: Partial<StripeAchPaymentSourceModel>
  ) {
    if (!stripe || !elements) {
      return;
    }
    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      return;
    }

    elements.submit();

    const result = await stripe?.createToken(cardElement, {
      name: data.account_holder_name,
      address_line1: data.address_line1,
      address_city: data.address_city,
      address_state: data.address_state,
      address_zip: data.address_zip,
      address_country: data.address_country,
    });

    if (result?.error) {
      setSnackbar({
        open: true,
        severity: 'error',
        message:
          result.error.message ||
          'There was an issue generating a stripe token.',
      });
    } else {
      setSourceToken(result?.token?.id);
      return result?.token?.id;
    }
  }

  const onSubmit: SubmitHandler<Partial<StripeAchPaymentSourceModel>> = async (
    data
  ) => {
    if (
      isAbleToSeeUIElement(
        changeLogRoles,
        BillingRoles.canAddPaymentMethods.value
      )
    ) {
      setOpenChangeLogDialog(true);

      if (paymentMethod === 'card') {
        const token = await getStripeTokenForCC(data);
        setSourceToken(token || '');
      }
    } else {
      if (paymentMethod === 'ach') {
        mutateAch(data);
      } else {
        const token = await getStripeTokenForCC(data);
        if (token) {
          mutate(token);
        }
      }
    }
  };

  return (
    <SharedDialog
      open={open}
      handleClose={handleClose}
      title="Add Payment Method"
      maxWidth="md"
      dialogContent={
        <>
          <Box sx={{ minHeight: 500 }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <RadioGroup
                value={paymentMethod}
                onChange={(event) =>
                  setPaymentMethod(event.target.value as 'card' | 'ach')
                }
                row
              >
                <Tooltip
                  title={
                    !creditCardPaymentsEnabled
                      ? 'Credit card payments are disabled'
                      : ''
                  }
                  arrow
                >
                  <FormControlLabel
                    value="card"
                    control={<Radio disabled={!creditCardPaymentsEnabled} />}
                    label="Card"
                  />
                </Tooltip>
                <FormControlLabel
                  value="ach"
                  control={<Radio />}
                  label="Bank Account"
                />
              </RadioGroup>
              <img
                src={
                  theme.palette.mode === 'light'
                    ? PoweredByStripe
                    : PoweredByStripeWhite
                }
                alt="Powered by Stripe"
                style={{ width: 120, height: 'auto' }}
              />
            </Stack>

            {paymentMethod === 'card' && (
              <AddCreditCardForm
                control={control}
                errors={errors}
                getValues={getValues}
                setValue={setValue}
                selectedCountry={selectedCountry || ''}
              />
            )}
            {paymentMethod === 'ach' && (
              <BankAccountPaymentMethodForm control={control} errors={errors} />
            )}
          </Box>
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={() => {
              if (paymentMethod === 'ach') {
                mutateAch(getValues());
              } else {
                mutate(sourceToken);
              }
            }}
            mutateLoading={isPending || isPendingAch}
          />
        </>
      }
      primaryButton={{
        loading: isPending || isPendingAch,
        action: handleSubmit(onSubmit),
        disabled: isPending || isPendingAch,
        title: 'Add Payment Method',
      }}
    />
  );
}
